import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Eye, Trash2, RefreshCw, Search, X } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, Modal } from './adminUtils';

export const TicketsTab = () => {
  const [adminTickets, setAdminTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketStats, setTicketStats] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cleaningTickets, setCleaningTickets] = useState(false);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value.slice(0, 100);
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(val.trim()), 300);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    clearTimeout(debounceRef.current);
  };

  const fetchAdminTickets = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (ticketFilter !== 'all') params.set('status_filter', ticketFilter);
      if (searchQuery) params.set('search', searchQuery);
      const [ticketsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/tickets?${params}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/admin/tickets/stats`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (ticketsRes.ok) setAdminTickets((await ticketsRes.json()).tickets);
      if (statsRes.ok) setTicketStats(await statsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [ticketFilter, searchQuery]);

  useEffect(() => { fetchAdminTickets(); }, [fetchAdminTickets]);

  const handleCleanupTickets = async () => {
    if (!window.confirm('Delete all closed tickets older than 90 days? This cannot be undone.')) return;
    setCleaningTickets(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tickets/cleanup`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}` }
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message); fetchAdminTickets(); }
      else throw new Error(d.detail || 'Failed');
    } catch (e) { toast.error(e.message); }
    finally { setCleaningTickets(false); }
  };

  const openTicketDetail = async (ticketId) => {
    setTicketDetailLoading(true); setTicketDetail(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tickets/${ticketId}`, { headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) setTicketDetail(await res.json());
    } catch (e) { toast.error('Failed to load'); }
    finally { setTicketDetailLoading(false); }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Delete this ticket permanently?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/tickets/${ticketId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getAdminToken()}` }
      });
      if (res.ok) { toast.success('Ticket deleted'); fetchAdminTickets(); }
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <>
      <div className="space-y-4">
        {ticketStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center"><p className="text-xs text-amber-300">Open</p><p className="text-xl font-bold text-white">{ticketStats.open}</p></div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center"><p className="text-xs text-blue-300">In Progress</p><p className="text-xl font-bold text-white">{ticketStats.in_progress}</p></div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center"><p className="text-xs text-emerald-300">Closed</p><p className="text-xl font-bold text-white">{ticketStats.closed}</p></div>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center"><p className="text-xs text-red-300">Urgent</p><p className="text-xl font-bold text-white">{ticketStats.urgent || 0}</p></div>
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3 text-center"><p className="text-xs text-orange-300">Unassigned</p><p className="text-xl font-bold text-white">{ticketStats.unassigned || 0}</p></div>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-center"><p className="text-xs text-purple-300">Total</p><p className="text-xl font-bold text-white">{ticketStats.total}</p></div>
          </div>
        )}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white flex items-center gap-2"><MessageSquare className="w-5 h-5 text-teal-400" />Support Tickets</CardTitle>
              <div className="flex gap-2 flex-wrap items-center">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={handleSearchChange}
                    placeholder="Search subject, email, #no..."
                    className="pl-7 pr-7 py-1 text-xs rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 w-52"
                    data-testid="ticket-search-input"
                  />
                  {searchInput && (
                    <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <Button size="sm" onClick={handleCleanupTickets} disabled={cleaningTickets} className="bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/20" data-testid="cleanup-tickets-btn">
                  <Trash2 className="w-3 h-3 mr-1" />{cleaningTickets ? 'Cleaning...' : 'Cleanup 90d+'}
                </Button>
                {['all', 'open', 'in_progress', 'closed'].map(f => (
                  <button key={f} onClick={() => setTicketFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-medium ${ticketFilter === f ? 'bg-teal-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}>
                    {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={fetchAdminTickets} className="border-white/20 text-white"><RefreshCw className="w-3 h-3" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-teal-500 animate-spin" /></div> : adminTickets.length === 0 ? (
              <p className="text-slate-400 text-center py-8">{searchQuery ? `No tickets found for "${searchQuery}"` : 'No tickets'}</p>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">#</TableHead><TableHead className="text-slate-300">Subject</TableHead><TableHead className="text-slate-300">User</TableHead><TableHead className="text-slate-300">Priority</TableHead><TableHead className="text-slate-300">Status</TableHead><TableHead className="text-slate-300">Handled By</TableHead><TableHead className="text-slate-300">Updated</TableHead><TableHead className="text-slate-300 text-center">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>{adminTickets.map(t => (
                    <TableRow key={t.id} className={`border-white/10 ${t.priority === 'urgent' ? 'bg-red-500/5' : t.priority === 'high' ? 'bg-orange-500/5' : ''}`}>
                      <TableCell className="text-slate-500 font-mono text-xs">#{t.ticket_no}</TableCell>
                      <TableCell className="text-white font-medium max-w-[180px] truncate">{t.subject}</TableCell>
                      <TableCell className="text-slate-300 text-sm">{t.user_email}</TableCell>
                      <TableCell><Badge className={t.priority === 'urgent' ? 'bg-red-500/20 text-red-300' : t.priority === 'high' ? 'bg-orange-500/20 text-orange-300' : t.priority === 'medium' ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-500/20 text-slate-300'}>{t.priority ? t.priority.charAt(0).toUpperCase() + t.priority.slice(1) : 'Medium'}</Badge></TableCell>
                      <TableCell><Badge className={t.status === 'open' ? 'bg-amber-500/20 text-amber-300' : t.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'}>{t.status === 'in_progress' ? 'In Progress' : t.status.charAt(0).toUpperCase() + t.status.slice(1)}</Badge></TableCell>
                      <TableCell className="text-sm">{t.assigned_to ? <span className="text-teal-400 font-medium">{t.assigned_to.agent_name}</span> : <span className="text-slate-600">Unassigned</span>}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{formatDate(t.updated_at)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => openTicketDetail(t.id)} className="border-white/20 text-slate-300"><Eye className="w-3 h-3" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteTicket(t.id)} className="bg-red-500/20 hover:bg-red-500/30 text-red-300"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ticket Detail Modal */}
      <Modal show={!!ticketDetail || ticketDetailLoading} onClose={() => setTicketDetail(null)} title={ticketDetail ? `Ticket #${ticketDetail.ticket_no} — ${ticketDetail.subject}` : 'Loading...'}>
        {ticketDetailLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 text-teal-500 animate-spin" /></div> : ticketDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">User</p><p className="text-white">{ticketDetail.user_email}</p></div>
              <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">Status / Priority</p><div className="flex gap-2 mt-1"><Badge className={ticketDetail.status === 'open' ? 'bg-amber-500/20 text-amber-300' : ticketDetail.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'}>{ticketDetail.status}</Badge><Badge className={ticketDetail.priority === 'urgent' ? 'bg-red-500/20 text-red-300' : ticketDetail.priority === 'high' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300'}>{ticketDetail.priority}</Badge></div></div>
              {ticketDetail.assigned_to && <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">Handled By</p><p className="text-teal-400 font-medium">{ticketDetail.assigned_to.agent_name}</p></div>}
              <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">Created</p><p className="text-white text-sm">{formatDate(ticketDetail.created_at)}</p></div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Conversation ({ticketDetail.messages.length} messages)</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {ticketDetail.messages.map((m, i) => (
                  <div key={i} className={`rounded-lg p-3 ${m.by === 'student' ? 'bg-white/5 border-l-2 border-l-amber-500' : 'bg-teal-500/10 border-l-2 border-l-teal-500'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${m.by === 'student' ? 'text-amber-400' : 'text-teal-400'}`}>{m.by === 'student' ? (m.sender_name || ticketDetail.user_name || 'Student') : `${m.sender_name || 'Support'} (Agent)`}</span>
                      <span className="text-xs text-slate-500">{formatDate(m.timestamp)}</span>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
            </div>
            {ticketDetail.internal_notes && ticketDetail.internal_notes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-amber-400 mb-2">Internal Notes ({ticketDetail.internal_notes.length})</h3>
                <div className="space-y-2">
                  {ticketDetail.internal_notes.map((n, i) => (
                    <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-amber-400">{n.agent_name}</span>
                        <span className="text-xs text-slate-500">{formatDate(n.timestamp)}</span>
                      </div>
                      <p className="text-sm text-slate-300">{n.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </>
  );
};
